"use client";

import { useEffect, useState } from "react";
import Comparison from "@/components/preview/comparison";
import ReplayIcon from "@/components/preview/replay-icon";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type Phase = "idle" | "pressing" | "stuck";

const PRESS_MS = 400;
const RESTART_MS = 150;

function TapButton({
  highlighted,
  onTap,
  pressing,
}: {
  highlighted: boolean;
  onTap: () => void;
  pressing: boolean;
}) {
  return (
    <Button
      className={cn(highlighted && "bg-gray-300", pressing && "scale-[0.97]")}
      onClick={onTap}
      rounded
      size="md"
      static
      type="button"
      variant="secondary"
    >
      Bookmark
    </Button>
  );
}

export default function AccessibilityHoverMediaDemo() {
  const [phase, setPhase] = useState<Phase>("idle");
  const [turns, setTurns] = useState(0);

  useEffect(() => {
    if (turns === 0) {
      return;
    }
    const press = setTimeout(() => setPhase("pressing"), RESTART_MS);
    const stick = setTimeout(() => setPhase("stuck"), RESTART_MS + PRESS_MS);
    return () => {
      clearTimeout(press);
      clearTimeout(stick);
    };
  }, [turns]);

  const run = () => {
    setPhase("idle");
    setTurns((count) => count + 1);
  };

  const pressing = phase === "pressing";

  return (
    <Comparison
      bad={
        <TapButton
          highlighted={phase !== "idle"}
          onTap={run}
          pressing={pressing}
        />
      }
      good={
        <TapButton highlighted={pressing} onTap={run} pressing={pressing} />
      }
      panelClassName="h-36"
      topBar={
        <Button
          leftIcon={
            <ReplayIcon className="size-4.5 text-gray-1000" turns={turns} />
          }
          onClick={run}
          rounded
          size="sm"
          type="button"
          variant="secondary"
        >
          Simulate click
        </Button>
      }
    />
  );
}
